import { Options } from 'baseui/select';
import LoadingComponent from 'components/common/loading';
import { useFormState } from 'components/elements/form';
import TableInput from 'components/elements/table/table-input';
import {
  ActionType,
  ColumnDataType,
  IColumn,
} from 'components/elements/table/type';
import { differenceInCalendarDays } from 'date-fns';
import useGetHotelRoom from 'hooks/use-get-hotel-room';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { TourHotelDetailFormType, TourListFormType } from '../form-type';
import { onAddNewHotelDetail } from '../hooks/use-get-default-values';

interface Props {
  parentIndex: number;
}

function useGetHotelTableList(roomOptions: Options[]) {
  const { t } = useTranslation();
  const { editable } = useFormState();
  const columns: IColumn<TourHotelDetailFormType>[] = React.useMemo(() => {
    return [
      {
        header: t('common:check_in'),
        accessorKey: 'checkIn',
        minSize: 100,
        dataType: ColumnDataType.date,
        required: true,
      },

      {
        header: t('common:check_out'),
        accessorKey: 'checkOut',
        minSize: 100,
        dataType: ColumnDataType.date,
        required: true,
      },
      {
        header: t('common:night'),
        accessorKey: 'night',
        minSize: 100,
        dataType: ColumnDataType.number,
        minNumber: 0,
        disabled: true,
      },
      {
        header: t('common:room_type'),
        accessorKey: 'hotelRoomId',
        minSize: 100,
        dataType: ColumnDataType.select,
        options: roomOptions,
        required: true,
      },
      {
        header: t('common:city_tax'),
        accessorKey: 'cityTax',
        minSize: 100,
        dataType: ColumnDataType.text,
      },
      {
        header: t('common:description'),
        accessorKey: 'description',
        minSize: 100,
        dataType: ColumnDataType.text,
      },
      ...(editable
        ? [
            {
              header: '',
              accessorKey: 'delete',
              minSize: 60,
              size: 60,
              maxSize: 60,
              dataType: ColumnDataType.delete,
            },
          ]
        : []),
    ];
  }, [editable, roomOptions, t]);

  return columns;
}

export default function HotelTable(props: Props) {
  const { editable } = useFormState();
  const [isShow, setIsShow] = React.useState(true);
  const { clearErrors, getValues, trigger, control } =
    useFormContext<TourListFormType>();

  const name = `tourHotels.${props.parentIndex}.details` as const;

  const { fields, update, remove, append } = useFieldArray({
    control,
    name,
    keyName: '__customId',
  });

  const { hotelId, hotelRooms, setHotelRooms, isFetching } = useGetHotelRoom({
    name: `tourHotels.${props.parentIndex}.hotelId`,
  });

  React.useEffect(() => {
    if (!hotelId || hotelId === '') {
      setHotelRooms([]);
      setIsShow(false);
      setTimeout(() => {
        setIsShow(true);
      }, 200);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotelId]);

  React.useEffect(() => {
    if (isFetching) {
      setIsShow(false);
    } else {
      setIsShow(true);
    }
  }, [isFetching]);

  const columns = useGetHotelTableList(hotelRooms);

  const dispatch = React.useCallback(
    (action) => {
      const details = getValues(name);
      const type = action.type as ActionType;
      const columnId = action.columnId as keyof TourHotelDetailFormType;
      const value = action.value;
      const currentRowIndex = action.rowIndex;
      const detail = details[currentRowIndex];

      const otherVal: () => Partial<TourHotelDetailFormType> = () => {
        try {
          if (columnId === 'checkIn') {
            const checkOutVal = detail.checkOut;
            if (checkOutVal && checkOutVal > value) {
              return {
                night: differenceInCalendarDays(checkOutVal, value),
              };
            }
          }

          if (columnId === 'checkOut') {
            const checkInVal = detail.checkIn;
            if (checkInVal && checkInVal < value) {
              return {
                night: differenceInCalendarDays(value, checkInVal),
              };
            }
          }
        } catch {
          return {};
        }

        return {};
      };

      if (type === 'addRow') {
        append(onAddNewHotelDetail());
      } else if (type === 'removeRow') {
        remove(currentRowIndex);
        trigger(name);
      } else if (type === 'update_cell') {
        update(currentRowIndex, {
          ...detail,
          [columnId]: value,
          ...otherVal(),
        });
        clearErrors(`${name}.${currentRowIndex}`);
      }
    },
    [append, clearErrors, getValues, name, remove, trigger, update],
  );

  if (isFetching || !isShow) {
    return <LoadingComponent style={{ height: 100, width: '100%' }} />;
  }

  return (
    <div style={{ width: '100' }}>
      <TableInput
        columns={columns}
        data={fields}
        dispatch={dispatch}
        disabled={!editable}
        name={name}
        uniqueRowKey="customId"
      />
    </div>
  );
}
